// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { areModuleNamesEqual, sanitizeModuleName } from '../utils/modules';

const doesNameAlreadyExist = (modules, newModuleName) =>
  modules.some((module) => areModuleNamesEqual(module, newModuleName));

export const AddModuleDialog = ({ modules = [], open, onClose, onAddModule }) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [newModuleName, setNewModuleName] = useState('');

  const onChange = useCallback(
    (moduleName) => {
      setNewModuleName(moduleName);

      const sanitizedModuleName = sanitizeModuleName(moduleName);
      if (doesNameAlreadyExist(modules, sanitizedModuleName))
        setError(
          t(
            'customcomponents.interactiveMap.toolbar.newModuleDialog.errors.alreadyExists',
            'A module with this name already exists'
          )
        );
      else if (sanitizedModuleName.length === 0)
        setError(
          t(
            'customcomponents.interactiveMap.toolbar.newModuleDialog.errors.cannotBeEmpty',
            'This value cannot be empty'
          )
        );
      else setError(null);
    },
    [modules, t]
  );

  const handleCloseDialog = useCallback(() => {
    setNewModuleName('');
    onClose();
  }, [onClose]);

  const handleAddModule = useCallback(() => {
    if (newModuleName.trim()) {
      onAddModule(newModuleName);
      setNewModuleName('');
    }
  }, [newModuleName, onAddModule]);

  return (
    <Dialog
      container={() => document.getElementById('map-and-panels-container')}
      open={open}
      onClose={handleCloseDialog}
    >
      <DialogTitle>Ajouter un nouveau module</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Nom du module"
          type="text"
          fullWidth
          value={newModuleName}
          onChange={(e) => onChange(e.target.value)}
          error={error != null}
          helperText={error}
          data-cy="module-name-input"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} data-cy="cancel-add-module">
          Annuler
        </Button>
        <Button disabled={error != null} onClick={handleAddModule} variant="contained" data-cy="add-module-submit">
          Ajouter
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddModuleDialog.propTypes = {
  modules: PropTypes.arrayOf(PropTypes.string).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddModule: PropTypes.func.isRequired,
};
